import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { AssetsService } from 'src/app/services/assets.service';
import { AlertService } from 'src/app/services/alert.service';
import { LABEL_SCHEMA_FIELDS } from 'src/app/models/label-schema';
import { IAssetLabel } from 'src/app/models/assetLabel';
import {
  FieldType,
  LabelField,
  LabelEditorDialogData,
  SCHEMA_SUBFIELDS,
  PRIMITIVE_ARRAY_FIELDS,
  OBJECT_ARRAY_FIELDS,
  ARRAY_ITEM_SCHEMA,
  JSON_FIELDS,
  DICTIONARY_FIELDS,
  OBJECT_FIELDS,
  NUMERIC_FIELDS,
  BOOLEAN_FIELDS,
  READONLY_FIELDS,
} from 'src/app/models/label-editor.model';

@Component({
  selector: 'app-label-editor',
  templateUrl: './label-editor.component.html',
  styleUrls: ['./label-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class LabelEditorComponent {
  assetLabel: IAssetLabel;
  assetId: string;
  assetType: string;
  saving = false;
  labelFields: LabelField[] = [];
  availableFields: string[] = [];
  selectedNewField = '';
  form = new FormGroup<Record<string, FormControl<string>>>({});
  newArrayItemValues: Record<string, string> = {};
  newCustomKeys: Record<string, string> = {};
  highlightedPath = '';

  getControl(path: string): FormControl<string> {
    return (this.form.controls[path] as FormControl<string>) ?? new FormControl<string>('', { nonNullable: true });
  }

  isDictionaryField(field: { key: string }): boolean {
    return DICTIONARY_FIELDS.has(field.key);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LabelEditorDialogData,
    public dialogRef: MatDialogRef<LabelEditorComponent>,
    private assetsService: AssetsService,
    private alertService: AlertService,
  ) {
    this.assetLabel = data.assetLabel;
    this.assetId = data.assetId;
    this.assetType = data.assetType;
    this.loadLabelIntoForm();
  }

  loadLabelIntoForm(): void {
    const label = this.assetLabel?.label;
    if (label) {
      const normalizedLabel = this.normalizeKeys(label);

      this.labelFields = Object.keys(normalizedLabel)
        .filter((key) => normalizedLabel[key] != null)
        .sort((a, b) => a.localeCompare(b))
        .map((key) => this.createLabelField(key, key, normalizedLabel[key]));
    }

    this.updateAvailableFields();
  }

  private toCamelCase(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  private toPascalCase(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private applyCasing(newValue: string, existingValues: string[]): string {
    if (existingValues.length === 0) {
      return newValue;
    }

    const sample = existingValues[0];
    const casing = this.detectCasing(sample);

    switch (casing) {
      case 'upper':
        return newValue.toUpperCase();
      case 'lower':
        return newValue.toLowerCase();
      case 'pascal':
        return newValue.charAt(0).toUpperCase() + newValue.slice(1).toLowerCase();
      case 'camel':
        return newValue.charAt(0).toLowerCase() + newValue.slice(1).toLowerCase();
      default:
        return newValue;
    }
  }

  private detectCasing(str: string): 'upper' | 'lower' | 'pascal' | 'camel' | 'mixed' {
    if (!str || str.length === 0) return 'mixed';

    const isAllUpper = str === str.toUpperCase();
    const isAllLower = str === str.toLowerCase();
    const isFirstUpper = str.charAt(0) === str.charAt(0).toUpperCase();
    const isFirstLower = str.charAt(0) === str.charAt(0).toLowerCase();

    if (isAllUpper) return 'upper';
    if (isAllLower) return 'lower';
    if (isFirstUpper && !isAllUpper) return 'pascal';
    if (isFirstLower && !isAllLower) return 'camel';
    return 'mixed';
  }

  private normalizeKeys(obj: Record<string, unknown>): Record<string, unknown> {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = this.toCamelCase(key);
      const value = obj[key];
      acc[camelKey] = this.normalizeValue(value);
      return acc;
    }, {} as Record<string, unknown>);
  }

  private normalizeValue(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeValue(item));
    }
    if (typeof value === 'object') {
      return this.normalizeKeys(value as Record<string, unknown>);
    }
    // Keep string/number/boolean values as-is
    return value;
  }

  createLabelField(key: string, path: string, value: unknown, schemaKey?: string, parentReadonly = false): LabelField {
    const effectiveSchemaKey = schemaKey ?? key;
    const isReadonly = parentReadonly || (READONLY_FIELDS as string[]).includes(key);
    const isArray = Array.isArray(value);
    const isObject = typeof value === 'object' && value !== null && !isArray;
    const isPrimitiveArrayField = PRIMITIVE_ARRAY_FIELDS.has(effectiveSchemaKey);
    const isObjectArrayField = OBJECT_ARRAY_FIELDS.has(effectiveSchemaKey);
    const isJsonField = JSON_FIELDS.has(effectiveSchemaKey);
    const isDictionaryField = DICTIONARY_FIELDS.has(effectiveSchemaKey);
    const isKnownObjectField = OBJECT_FIELDS.has(effectiveSchemaKey);
    // Dictionary/object fields with null/undefined become objects; existing primitives stay as primitives
    const isEmptyObjectField = (isDictionaryField || isKnownObjectField) && (value === null || value === undefined);

    const type: FieldType = isJsonField ? 'primitive'
      : isPrimitiveArrayField ? 'primitiveArray'
      : isObjectArrayField ? 'objectArray'
      : isEmptyObjectField ? 'object'
      : isObject ? 'object'
      : isArray ? (this.isPrimitiveArray(value as unknown[]) ? 'primitiveArray' : 'objectArray')
      : 'primitive';

    const children = this.createChildren(effectiveSchemaKey, path, value, type, isReadonly);
    const formValue = isJsonField && isObject ? JSON.stringify(value) : value;
    this.registerFormControl(path, formValue, type, isReadonly);

    return { key, path, type, isReadonly, children };
  }

  private isPrimitiveArray(arr: unknown[]): boolean {
    return arr.length === 0 || typeof arr[0] !== 'object' || arr[0] === null;
  }

  private createChildren(parentKey: string, path: string, value: unknown, type: FieldType, parentReadonly = false): LabelField[] | undefined {
    const handlers: Record<FieldType, () => LabelField[] | undefined> = {
      object: () => {
        const obj = (value as Record<string, unknown>) ?? {};
        const schemaKeys = SCHEMA_SUBFIELDS[parentKey] ?? [];
        const dataKeys = Object.keys(obj);
        const allKeys = [...new Set([...schemaKeys, ...dataKeys])].sort((a, b) => a.localeCompare(b));
        return allKeys.map((subKey) => this.createLabelField(subKey, `${path}.${subKey}`, obj[subKey] ?? null, undefined, parentReadonly));
      },
      primitiveArray: () => {
        const arr = Array.isArray(value) ? value : [];
        return arr.map((item, i) => this.createLabelField(`${i}`, `${path}[${i}]`, item, undefined, parentReadonly));
      },
      objectArray: () => {
        const arr = Array.isArray(value) ? value : [];
        const itemSchemaKey = ARRAY_ITEM_SCHEMA[parentKey];
        return arr.map((item, i) => this.createLabelField(`${i}`, `${path}[${i}]`, item, itemSchemaKey, parentReadonly));
      },
      primitive: () => undefined,
    };
    return handlers[type]();
  }

  private registerFormControl(path: string, value: unknown, type: FieldType, disabled = false): void {
    const shouldRegister = type === 'primitive';
    if (!shouldRegister) return;

    const stringValue = this.stripQuotes(String(value ?? ''));
    const control = new FormControl<string>({ value: stringValue, disabled }, { nonNullable: true });
    this.form.addControl(path, control);
  }

  private stripQuotes(value: string): string {
    if (value.startsWith('"') && value.endsWith('"') && value.length >= 2) {
      return value.slice(1, -1);
    }
    return value;
  }

  updateAvailableFields(): void {
    const existingKeys = new Set(this.labelFields.map((f) => f.key));
    this.availableFields = LABEL_SCHEMA_FIELDS
      .filter((f) => !existingKeys.has(f))
      .sort((a, b) => a.localeCompare(b));
  }

  removeTopLevelField(index: number): void {
    const field = this.labelFields[index];
    const confirmed = window.confirm(`Remove field "${field.key}"?`);
    if (!confirmed) return;

    this.removeFieldControls(field);
    this.labelFields = this.labelFields.filter((_, i) => i !== index);
    this.updateAvailableFields();
  }

  addField(): void {
    if (!this.selectedNewField) return;

    const key = this.selectedNewField;
    const initialValue = DICTIONARY_FIELDS.has(key) ? {}
      : OBJECT_FIELDS.has(key) ? {}
      : OBJECT_ARRAY_FIELDS.has(key) ? []
      : PRIMITIVE_ARRAY_FIELDS.has(key) ? []
      : null;
    const newField = this.createLabelField(key, key, initialValue);
    this.labelFields = [...this.labelFields, newField].sort((a, b) => a.key.localeCompare(b.key));
    this.selectedNewField = '';
    this.updateAvailableFields();

    this.highlightItem(`field-${key}`, true);
  }

  addArrayItem(field: LabelField): void {
    const isPrimitive = field.type === 'primitiveArray';

    if (isPrimitive) {
      const value = this.newArrayItemValues[field.path] ?? '';
      if (!value.trim()) return;

      this.rebuildPrimitiveArray(field, value);
      this.newArrayItemValues[field.path] = '';
    } else {
      const currentChildren = field.children ?? [];
      const newIndex = currentChildren.length;
      const itemPath = `${field.path}[${newIndex}]`;
      const itemSchemaKey = ARRAY_ITEM_SCHEMA[field.key];
      const schemaFields = SCHEMA_SUBFIELDS[itemSchemaKey] ?? [];
      const children = schemaFields.map((subKey: string) => this.createLabelField(subKey, `${itemPath}.${subKey}`, null));
      const newItem: LabelField = { key: `${newIndex}`, path: itemPath, type: 'object', isReadonly: false, children };
      field.children = [...currentChildren, newItem];
      this.highlightItem(itemPath);
    }
  }

  addCustomKey(field: LabelField): void {
    const customKey = this.newCustomKeys[field.path]?.trim();
    if (!customKey) return;

    const currentChildren = field.children ?? [];
    const existingKeys = new Set(currentChildren.map((c: LabelField) => c.key));
    if (existingKeys.has(customKey)) {
      return; // Key already exists
    }

    const newField = this.createLabelField(customKey, `${field.path}.${customKey}`, '');
    field.children = [...currentChildren, newField].sort((a, b) => a.key.localeCompare(b.key));
    this.newCustomKeys[field.path] = '';
    this.highlightItem(`${field.path}.${customKey}`, true);
  }

  private rebuildPrimitiveArray(field: LabelField, newValue: string): void {
    const currentChildren = field.children ?? [];

    // Get existing values to infer casing
    const existingValues = currentChildren
      .map((child: LabelField) => this.form.controls[child.path]?.value ?? '')
      .filter((v: string) => v.trim());

    // Apply inferred casing to new value
    const casedNewValue = this.applyCasing(newValue, existingValues);

    // Get all current values and add new one
    const values = existingValues
      .concat(casedNewValue)
      .sort((a: string, b: string) => a.localeCompare(b));

    // Remove old controls
    currentChildren.forEach((child: LabelField) => (this.form as FormGroup).removeControl(child.path));

    // Create new sorted children
    field.children = values.map((value: string, i: number) => {
      const itemPath = `${field.path}[${i}]`;
      this.form.addControl(itemPath, new FormControl<string>(value, { nonNullable: true }));
      return { key: `${i}`, path: itemPath, type: 'primitive' as FieldType, isReadonly: false };
    });

    // Highlight the newly added value (no scroll for sorted arrays)
    const newIndex = values.indexOf(casedNewValue);
    this.highlightItem(`item-${field.path}[${newIndex}]`, false);
  }

  private highlightItem(id: string, scroll = false): void {
    this.highlightedPath = id;
    if (scroll) {
      setTimeout(() => {
        const element = document.getElementById(id);
        const container = document.querySelector('mat-dialog-content');
        if (element && container) {
          const elementTop = element.offsetTop;
          container.scrollTo({ top: elementTop - 10, behavior: 'smooth' });
        }
      }, 100);
    }
    setTimeout(() => {
      this.highlightedPath = '';
    }, 2000);
  }

  moveFieldUp(fields: LabelField[], index: number): void {
    if (index <= 0) return;
    const newFields = [...fields];
    [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
    this.replaceFields(fields, newFields);
  }

  moveFieldDown(fields: LabelField[], index: number): void {
    if (index >= fields.length - 1) return;
    const newFields = [...fields];
    [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    this.replaceFields(fields, newFields);
  }

  private replaceFields(oldFields: LabelField[], newFields: LabelField[]): void {
    oldFields.length = 0;
    newFields.forEach((f) => oldFields.push(f));
  }

  removeSubField(fields: LabelField[], index: number): void {
    const field = fields[index];
    const confirmed = window.confirm(`Remove field "${field.key}"?`);
    if (!confirmed) return;

    this.removeFieldControls(field);
    const newFields = fields.filter((_, i) => i !== index);
    this.replaceFields(fields, newFields);

    if (fields === this.labelFields) {
      this.updateAvailableFields();
    }
  }

  private removeFieldControls(field: LabelField): void {
    const hasChildren = field.type === 'object' || field.type === 'primitiveArray' || field.type === 'objectArray';
    if (hasChildren && field.children) {
      field.children.forEach((child: LabelField) => this.removeFieldControls(child));
    } else {
      (this.form as FormGroup).removeControl(field.path);
    }
  }

  onSave(): void {
    this.saving = true;
    const updatedLabel = this.labelFields.reduce((acc, field) => {
      acc[this.toPascalCase(field.key)] = this.buildFieldValue(field);
      return acc;
    }, {} as Record<string, unknown>);

    this.assetsService.saveAssetLabel(this.assetId, this.assetType, updatedLabel).subscribe({
      next: () => {
        this.alertService.successToast('Label saved');
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: () => {
        this.alertService.errorToast('Failed to save label');
        this.saving = false;
      },
    });
  }

  private buildFieldValue(field: LabelField): unknown {
    const handlers: Record<FieldType, () => unknown> = {
      object: () => (field.children ?? []).reduce((acc: Record<string, unknown>, child: LabelField) => {
        acc[this.toPascalCase(child.key)] = this.buildFieldValue(child);
        return acc;
      }, {} as Record<string, unknown>),
      primitiveArray: () => (field.children ?? []).map((item: LabelField) => this.buildFieldValue(item)),
      objectArray: () => (field.children ?? []).map((item: LabelField) => this.buildFieldValue(item)),
      primitive: () => this.convertPrimitiveValue(field.key, this.form.controls[field.path]?.value ?? ''),
    };
    return handlers[field.type]();
  }

  private convertPrimitiveValue(key: string, value: string): unknown {
    const cleaned = this.stripQuotes(value);
    if (!cleaned.trim()) {
      return NUMERIC_FIELDS.has(key) || BOOLEAN_FIELDS.has(key) ? null : '';
    }
    if (NUMERIC_FIELDS.has(key)) {
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    }
    if (BOOLEAN_FIELDS.has(key)) {
      const lower = cleaned.toLowerCase();
      return lower === 'true' || lower === '1' || lower === 'yes';
    }
    return cleaned;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
